import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { listMyEvents, listMyInvites } from '../api/owner'
import type { MyEvent } from '../api/ownerTypes'
import { LoadingScreen } from '../components/LoadingScreen'
import { ThemeChoice } from '../components/ThemeChoice'
import { MyInvites } from './components/MyInvites'
import { GuestAvatar } from '../components/Monogram'
import { formatEventDate, plural } from '../lib/format'
import { useOwner } from './context'
import { Logo } from '../components/Logo'
import { QrGlyph } from './components/QrCode'
import { StatusBadge } from './components/StatusBadge'

/**
 * Ronde 20A, 20B, 20D en 20Q — "Mijn events".
 *
 * Eén pagina: op 390px een kolom met de knop onderaan vastgezet, vanaf lg een
 * kop met de knop rechts en de kaarten naast elkaar.
 */
export function MyEventsPage() {
  const { owner } = useOwner()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)

  const events = useQuery({ queryKey: ['owner', 'events'], queryFn: listMyEvents })

  if (events.isPending) {
    return <LoadingScreen label="Je events ophalen…" />
  }

  const list = events.data?.events ?? []
  const firstName = owner.displayName.split(' ')[0]

  return (
    <div className="flex min-h-full flex-col lg:h-full">
      {/* --- Balk, alleen desktop (ronde 20Q) --------------------------- */}
      <header className="border-line-soft hidden items-center justify-between border-b px-12 py-6.5 lg:flex">
        <Logo />
        <div className="relative flex items-center gap-3">
          <span className="text-ink-muted text-[15px]">{owner.displayName}</span>
          <button
            type="button"
            aria-label="Jouw menu"
            data-menu-anchor
            onClick={() => setMenuOpen((open) => !open)}
          >
            <GuestAvatar displayName={owner.displayName} size={38} ring={menuOpen} />
          </button>
          {menuOpen && <AccountMenu onClose={() => setMenuOpen(false)} anchor="right-0 top-13" />}
        </div>
      </header>

      {/* --- Kop -------------------------------------------------------- */}
      <div className="relative z-30 flex items-start justify-between px-5.5 pt-6.5 pb-4.5 lg:items-end lg:px-12 lg:pt-10 lg:pb-0">
        <div>
          <p className="text-ink-faint text-xs tracking-[0.18em] uppercase">
            Ingelogd als {firstName}
          </p>
          <h1 className="font-display mt-2 text-[38px] leading-[1.05] lg:text-[46px]">
            Mijn events
          </h1>
        </div>

        {/* Op mobiel de avatar met het menu; op desktop de actieknop. */}
        <div className="relative lg:hidden">
          <button
            type="button"
            aria-label="Jouw menu"
            data-menu-anchor
            onClick={() => setMenuOpen((open) => !open)}
          >
            <GuestAvatar displayName={owner.displayName} size={38} ring={menuOpen} />
          </button>
          {menuOpen && <AccountMenu onClose={() => setMenuOpen(false)} anchor="right-0 top-12" />}
        </div>

        <button
          type="button"
          onClick={() => navigate('/beheer/nieuw')}
          className="bg-accent text-accent-ink shadow-accent hidden h-13 items-center gap-2.5 rounded-full px-6.5 text-base font-semibold transition hover:brightness-95 lg:flex"
        >
          ＋ Nieuw event
        </button>
      </div>

      <MyInvites />

      {list.length === 0 ? (
        <EmptyState onCreate={() => navigate('/beheer/nieuw')} />
      ) : (
        <div className="flex-1 px-5.5 pt-1 pb-28 lg:px-12 lg:pt-8 lg:pb-12">
          <div className="flex flex-col gap-3.5 lg:flex-row lg:flex-wrap lg:items-start lg:gap-6">
            {list.map((subject) => (
              <EventCard
                key={subject.id}
                subject={subject}
                onManage={() => navigate(`/beheer/e/${subject.id}`)}
                onQr={() => navigate(`/beheer/e/${subject.id}/qr`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* De zwevende knop op mobiel (ronde 20A). */}
      {list.length > 0 && (
        <div className="from-shell fixed inset-x-0 bottom-0 bg-gradient-to-t from-62% to-transparent px-5.5 pt-4 pb-7.5 lg:hidden">
          <button
            type="button"
            onClick={() => navigate('/beheer/nieuw')}
            className="bg-accent text-accent-ink shadow-accent-lg flex h-14.5 w-full items-center justify-center gap-2.5 rounded-full text-[17px] font-semibold transition hover:brightness-95"
          >
            ＋ Nieuw event
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * De kaart van één event: beeld, tellingen en twee knoppen (ronde 20A, 20Q).
 *
 * Dezelfde voor allemaal. Het eerste event kreeg deze kaart en de rest een smal
 * rijtje, wat op een lijst van drie leest als "die ene is belangrijker" —
 * terwijl het alleen maar de bovenste is. Wie twee feesten beheert heeft er
 * geen hoofdfeest bij.
 *
 * Dat het de rijke kaart is en niet het rijtje: daar zat de cover in, de
 * QR-knop en de tellingen. Allemaal hetzelfde maken door dat weg te halen zou
 * de lijst gelijk én armer maken.
 */
function EventCard({
  subject,
  onManage,
  onQr,
}: {
  subject: MyEvent
  onManage: () => void
  onQr: () => void
}) {
  return (
    <article className="border-line-card bg-surface overflow-hidden rounded-[20px] border shadow-[0_8px_20px_rgba(42,35,32,0.07)] transition-shadow hover:shadow-[0_10px_26px_rgba(42,35,32,0.12)] lg:w-[420px]">
      <div className="placeholder-warm relative h-33 lg:h-49">
        {subject.coverUrl && (
          <img src={subject.coverUrl} alt="" className="h-full w-full object-cover" />
        )}

        <div className="absolute top-3 left-3 lg:top-3.5 lg:left-3.5">
          <StatusBadge isPrivate={subject.isPrivate} onSurface />
        </div>

        <button
          type="button"
          aria-label="QR-code bekijken"
          onClick={onQr}
          className="absolute right-3 bottom-3 lg:right-3.5 lg:bottom-3.5"
        >
          <QrGlyph size={44} />
        </button>
      </div>

      <div className="p-4 pb-3.5 lg:p-5 lg:pb-4.5">
        <h2 className="font-display text-[28px] leading-[1.1] lg:text-[32px]">{subject.title}</h2>
        <p className="text-ink-soft mt-1.5 text-[13px] lg:text-sm">{subtitleOf(subject)}</p>

        {/* Alleen bij een feest van iemand anders. Bij je eigen events zou "van
            jou" op elke kaart staan, en dan zegt het niets meer — juist door het
            weg te laten valt het op waar het er wél staat. */}
        {!subject.isCreator && (
          <p className="text-ink-faint mt-2 flex items-center gap-1.5 text-[13px]">
            <span aria-hidden="true">👤</span>
            Van {subject.ownerName}
          </p>
        )}

        {/* De tellingen staan alleen op mobiel; op desktop is de kaart smaller
            en zou de rij gaan wringen (ronde 20Q laat ze ook weg). */}
        <div className="border-line-menu mt-3.5 flex gap-5 border-t pt-3.5 lg:hidden">
          <Stat value={subject.mediaCount} label="foto’s" />
          <Stat value={subject.guestCount} label="gasten" />
          <Stat value={subject.challengeCount} label="challenges" />
        </div>

        <div className="mt-4 flex gap-2 lg:mt-5 lg:gap-2.5">
          <button
            type="button"
            onClick={onManage}
            className="bg-accent text-accent-ink h-11.5 flex-1 rounded-full text-[15px] font-semibold transition hover:brightness-95 lg:h-12"
          >
            Beheren
          </button>
          <button
            type="button"
            onClick={onQr}
            className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-11.5 w-25 rounded-full border text-[15px] transition-colors lg:h-12 lg:w-27.5"
          >
            QR
          </button>
        </div>
      </div>
    </article>
  )
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <p className="text-[19px] font-semibold">{value}</p>
      <p className="text-ink-faint mt-0.5 text-xs">{label}</p>
    </div>
  )
}

/** "22 augustus 2026 · Hoeve De Linde" of "Nog geen datum · 0 foto's". */
function subtitleOf(subject: MyEvent): string {
  if (subject.eventDateUtc) {
    return [formatEventDate(subject.eventDateUtc), subject.venue].filter(Boolean).join(' · ')
  }

  return `Nog geen datum · ${plural(subject.mediaCount, 'foto', 'foto’s')}`
}

/** Ronde 20B — nog geen event. */
function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col items-center justify-center px-10 text-center">
        <span
          aria-hidden="true"
          className="bg-tint grid size-23 grid-cols-3 grid-rows-3 gap-1 rounded-3xl p-4.5"
        >
          {[1, 0, 1, 0, 1, 0, 1, 0, 1].map((filled, index) => (
            <span key={index} className={filled ? 'bg-accent rounded-[2px]' : ''} />
          ))}
        </span>

        <h2 className="font-display mt-6.5 text-[32px] leading-[1.15]">Nog geen event</h2>
        <p className="text-ink-soft mt-2.5 text-[15px] leading-[1.55] text-pretty">
          Maak er een aan, dan krijg je meteen een QR-code die je op de tafels kan leggen.
        </p>
      </div>

      <div className="px-5.5 pb-7.5 lg:mx-auto lg:w-[420px] lg:pb-16">
        <button
          type="button"
          onClick={onCreate}
          className="bg-accent text-accent-ink shadow-accent-lg flex h-14.5 w-full items-center justify-center gap-2.5 rounded-full text-[17px] font-semibold transition hover:brightness-95"
        >
          ＋ Nieuw event
        </button>
      </div>
    </div>
  )
}

/** Ronde 20D — het accountmenu. */
function AccountMenu({ onClose, anchor }: { onClose: () => void; anchor: string }) {
  const { owner, signOut } = useOwner()
  const navigate = useNavigate()
  const menu = useRef<HTMLDivElement>(null)

  // Hetzelfde antwoord als de banner en de pagina eronder; TanStack deelt het,
  // dus dit kost geen extra verzoek.
  const invites = useQuery({ queryKey: ['owner', 'invites'], queryFn: listMyInvites })
  const open = invites.data?.invites.length ?? 0

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Element | null

      // Een druk ín een menu is niet "buiten": op elk scherm staan er twee, een
      // voor mobiel en een voor desktop, met één gedeelde open-stand. Ze zijn
      // allebei gemonteerd en luisteren allebei mee, maar één is met CSS
      // verborgen. Keek elk menu alleen naar zichzelf, dan zag die verborgen
      // tweeling een druk in de zichtbare als "buiten mij" en sloot hij alles —
      // op pointerdown, dus nog voor de klik bij de knop aankwam. Uitloggen
      // deed daardoor niets.
      //
      // De knop die het menu opent telt ook niet mee. Zou die wel meetellen,
      // dan sluit het menu bij het indrukken en opent het bij de klik erna, en
      // is het met dezelfde knop niet meer dicht te krijgen.
      if (target?.closest('[role="menu"], [data-menu-anchor]')) {
        return
      }

      onClose()
    }

    document.addEventListener('pointerdown', onPointerDown, true)

    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [onClose])

  return (
    <>
      <div className="bg-shell/55 fixed inset-0 z-30" aria-hidden="true" />

      <div
        ref={menu}
        role="menu"
        className={`rounded-card border-line-card bg-surface shadow-menu absolute z-40 w-62.5 border p-1.5 text-left ${anchor}`}
      >
        <div className="border-line-menu border-b px-3.5 pt-3 pb-3.5">
          <p className="text-base font-semibold">{owner.displayName}</p>
          <p className="text-ink-faint mt-0.75 text-[13px]">{owner.email}</p>
        </div>

        <ThemeChoice />

        <div className="flex flex-col pt-1">
          {/* Van waar je ook bent in het beheer: hier staan je uitnodigingen.
              Het getal erbij, want een menu-item dat je alleen ziet als je het
              opent is geen melding — en een uitnodiging waar iemand op wacht
              hoort er wel een te zijn. */}
          <button
            type="button"
            onClick={() => {
              onClose()
              navigate('/beheer/uitnodigingen')
            }}
            className="hover:bg-tint/50 flex cursor-pointer items-center gap-3 rounded-lg px-3.5 py-3.25 text-left text-[15px]"
          >
            <span aria-hidden="true" className="text-ink-soft w-4.5 text-center">
              ✉
            </span>
            Uitnodigingen
            {open > 0 && (
              <span className="bg-accent text-accent-ink ml-auto rounded-full px-2 py-0.5 text-[11px] font-bold">
                {open}
              </span>
            )}
          </button>

          {/* Accountgegevens hoort bij de inlogflow die nog komt. */}
          <button
            type="button"
            disabled
            className="text-ink-ghost flex items-center gap-3 rounded-lg px-3.5 py-3.25 text-left text-[15px]"
          >
            <span aria-hidden="true" className="w-4.5 text-center">
              ✎
            </span>
            Accountgegevens
          </button>

          <button
            type="button"
            onClick={() => void signOut()}
            className="border-line-menu text-accent mt-1 flex items-center gap-3 rounded-lg border-t px-3.5 py-3.25 text-left text-[15px]"
          >
            <span aria-hidden="true" className="w-4.5 text-center">
              →
            </span>
            Uitloggen
          </button>
        </div>
      </div>
    </>
  )
}
